import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  fetchAnnouncements,
  fetchFeedbackReplies,
  fetchLeaderboard,
  fetchNewsFeed,
  fetchUpcomingEvents,
  inspectDevice,
  isDefinitiveEnrollmentRevoke,
  leaveDevice,
  personalComplete,
  personalMatchName,
  personalVerifyBirthday,
  personalVerifyPartner,
  personalVerifyPhone,
  personalVerifyState,
  personalVerifyWedding,
  redeemInvite,
  refreshSession,
  requestAccess,
  requestAccessStatus,
  resumeDevice,
  inspectInvite,
  normalizeInviteCode,
  type AuthSessionResponse,
} from "../api/client";
import { decryptBundleJson, revokePhotoUrls } from "../crypto/bundleCrypto";
import type {
  Announcement,
  EnrolledSession,
  FamilyBundle,
  FeedbackReply,
  LeaderboardEntry,
  LockPhase,
  FamilyBrowseMode,
  MainTab,
  NewsFeed,
  PersonalMatch,
  Person,
  UpcomingEvent,
} from "../models/types";
import { clearDeviceId, deviceLabel, getDeviceId, rotateDeviceId } from "../storage/device";
import { normalizeBirthDateInput } from "../utils/birthDate";
import {
  clearPendingAccessRequest,
  clearSession,
  hasSeenOpening,
  isSessionExpired,
  loadPendingAccessRequest,
  loadSession,
  resetOpeningSeen,
  resetTabIntros,
  savePendingAccessRequest,
  saveSession,
  shouldSkipOpening,
} from "../storage/session";

interface AppContextValue {
  phase: LockPhase;
  isUnlocked: boolean;
  isWorking: boolean;
  lastError: string | null;
  session: EnrolledSession | null;
  bundle: FamilyBundle | null;
  newsFeed: NewsFeed | null;
  upcomingEvents: UpcomingEvent[];
  announcements: Announcement[];
  feedbackReplies: FeedbackReply[];
  leaderboardByMode: Record<string, LeaderboardEntry[]>;
  activeTab: MainTab;
  setActiveTab: (tab: MainTab) => void;
  familyBrowseMode: FamilyBrowseMode;
  setFamilyBrowseMode: (mode: FamilyBrowseMode) => void;
  selectedPersonId: string | null;
  setSelectedPersonId: (id: string | null) => void;
  showSettings: boolean;
  setShowSettings: (v: boolean) => void;
  showInvites: boolean;
  setShowInvites: (v: boolean) => void;
  showFeedback: boolean;
  setShowFeedback: (v: boolean) => void;
  showHeritage: boolean;
  setShowHeritage: (v: boolean) => void;
  bundleLoading: boolean;
  bundleLoadError: string | null;
  personStack: string[];
  pushPerson: (id: string) => void;
  popPerson: () => void;
  playInGame: boolean;
  setPlayInGame: (v: boolean) => void;
  showAddEvent: boolean;
  setShowAddEvent: (v: boolean) => void;
  showAddAnnouncement: boolean;
  setShowAddAnnouncement: (v: boolean) => void;
  replayOpening: () => void;
  replayHeritage: () => void;
  resetTabIntrosSeen: () => void;
  personalAvatarB64: string | null;
  personalStepMessage: string | null;
  pendingAccessRequestId: string | null;
  personalUnlockHelpMatch: PersonalMatch | null;
  showPersonalUnlockHelp: boolean;
  beginEnrollmentProbe: () => Promise<void>;
  showWelcome: () => void;
  showPersonalNameEntry: () => void;
  showInviteEntry: () => void;
  showAccessHelp: () => void;
  showAccessRequestForm: () => void;
  showPendingAccessRequest: () => void;
  matchPersonalName: (firstName: string, lastName: string) => Promise<void>;
  continuePersonalWelcome: (match: PersonalMatch) => void;
  continuePersonalVerifyIntro: (match: PersonalMatch) => void;
  submitPersonalBirthday: (match: PersonalMatch, value: string) => Promise<void>;
  submitPersonalPartner: (match: PersonalMatch, value: string) => Promise<void>;
  submitPersonalPhone: (match: PersonalMatch, value: string) => Promise<void>;
  submitPersonalState: (match: PersonalMatch, value: string) => Promise<void>;
  submitPersonalWedding: (match: PersonalMatch, value: string) => Promise<void>;
  submitPersonalUnlockHelp: (match: PersonalMatch, reason: string) => Promise<void>;
  inspectInviteCode: (code: string) => Promise<{ display_name: string; person_id: string; invite_kind: string; relationship_note?: string | null }>;
  redeemInviteCode: (code: string) => Promise<void>;
  submitAccessRequest: (payload: {
    requester_name: string;
    sharer_name: string;
    latitude: number;
    longitude: number;
    accuracy_m?: number | null;
    suggested_person_id?: string | null;
    reason?: string | null;
  }) => Promise<void>;
  pollAccessRequest: () => Promise<void>;
  resumeEnrolledDevice: () => Promise<void>;
  /** Leave server enrollment (best effort), clear local device id, open invite entry. */
  abandonEnrolledDevice: () => Promise<void>;
  continueIntoApp: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshAll: () => Promise<void>;
  refreshLeaderboard: (mode: string) => Promise<LeaderboardEntry[]>;
  person: (id: string) => Person | undefined;
  me: Person | undefined;
}

const AppContext = createContext<AppContextValue | null>(null);

function toSession(res: AuthSessionResponse): EnrolledSession {
  return {
    person_id: res.person_id,
    display_name: res.display_name,
    bundle_key_b64: res.bundle_key_b64,
    signing_secret_b64: res.signing_secret_b64,
    session_token: res.session_token,
    expires_at: res.expires_at,
  };
}

function isAlreadyRegisteredError(error: unknown): boolean {
  return error instanceof ApiError && error.code === "already_registered";
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<LockPhase>({ kind: "checkingEnrollment" });
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [session, setSession] = useState<EnrolledSession | null>(loadSession());
  const [bundle, setBundle] = useState<FamilyBundle | null>(null);
  const [newsFeed, setNewsFeed] = useState<NewsFeed | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [feedbackReplies, setFeedbackReplies] = useState<FeedbackReply[]>([]);
  const [leaderboardByMode, setLeaderboardByMode] = useState<
    Record<string, LeaderboardEntry[]>
  >({});
  const [activeTab, setActiveTab] = useState<MainTab>("home");
  const [familyBrowseMode, setFamilyBrowseMode] = useState<FamilyBrowseMode>("tree");
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showInvites, setShowInvites] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHeritage, setShowHeritage] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleLoadError, setBundleLoadError] = useState<string | null>(null);
  const [personStack, setPersonStack] = useState<string[]>([]);
  const [playInGame, setPlayInGame] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [personalAvatarB64, setPersonalAvatarB64] = useState<string | null>(null);
  const [personalStepMessage, setPersonalStepMessage] = useState<string | null>(null);
  const [pendingAccessRequestId, setPendingAccessRequestId] = useState<string | null>(
    loadPendingAccessRequest(),
  );
  const [personalVerifyFailureCount, setPersonalVerifyFailureCount] = useState(0);
  const [personalUnlockHelpMatch, setPersonalUnlockHelpMatch] = useState<PersonalMatch | null>(
    null,
  );

  const resetPersonalUnlockHelp = useCallback(() => {
    setPersonalVerifyFailureCount(0);
    setPersonalUnlockHelpMatch(null);
  }, []);

  const recordPersonalVerifyFailure = useCallback((match: PersonalMatch) => {
    setPersonalVerifyFailureCount((count) => count + 1);
    setPersonalUnlockHelpMatch(match);
  }, []);

  const showPersonalUnlockHelp =
    personalVerifyFailureCount >= 2 && personalUnlockHelpMatch !== null;

  const handlePersonalVerifyError = useCallback(
    (error: unknown, match: PersonalMatch) => {
      if (error instanceof ApiError && error.code === "personal_auth_locked") {
        setPersonalVerifyFailureCount((count) => Math.max(count, 3));
        setPersonalUnlockHelpMatch(match);
        setLastError(error.message);
        setPhase({ kind: "inviteEntry" });
        return;
      }
      recordPersonalVerifyFailure(match);
      setLastError(error instanceof Error ? error.message : "Verification failed.");
    },
    [recordPersonalVerifyFailure],
  );

  const loadBundle = useCallback(async (s: EnrolledSession) => {
    setBundleLoading(true);
    setBundleLoadError(null);
    try {
      const data = await decryptBundleJson<FamilyBundle>(s, s.bundle_key_b64);
      setBundle(data);
    } catch (e) {
      setBundleLoadError(e instanceof Error ? e.message : "Could not load family data.");
      setBundle(null);
    } finally {
      setBundleLoading(false);
    }
  }, []);

  const ensureFreshSession = useCallback(async (s: EnrolledSession) => {
    if (!isSessionExpired(s)) return s;
    try {
      const refreshed = toSession(await refreshSession(s));
      saveSession(refreshed);
      setSession(refreshed);
      return refreshed;
    } catch (error) {
      // Signature/session soft failures must not wipe enrollment — resume instead.
      if (isDefinitiveEnrollmentRevoke(error)) throw error;
      try {
        const resumed = toSession(await resumeDevice(getDeviceId()));
        saveSession(resumed);
        setSession(resumed);
        return resumed;
      } catch (resumeError) {
        if (isDefinitiveEnrollmentRevoke(resumeError)) throw resumeError;
        throw error;
      }
    }
  }, []);

  const refreshAll = useCallback(async () => {
    if (!session) return;
    const s = await ensureFreshSession(session);
    const [feed, events, anns, replies] = await Promise.all([
      fetchNewsFeed(s),
      fetchUpcomingEvents(s),
      fetchAnnouncements(s),
      fetchFeedbackReplies(s),
    ]);
    setNewsFeed(feed);
    setUpcomingEvents(events.events ?? []);
    setAnnouncements(anns.announcements ?? []);
    setFeedbackReplies((replies.replies ?? []).filter((r) => !r.read_at));
  }, [ensureFreshSession, session]);

  const refreshLeaderboard = useCallback(
    async (mode: string) => {
      if (!session) return [];
      const s = await ensureFreshSession(session);
      const data = await fetchLeaderboard(s, mode);
      setLeaderboardByMode((prev) => ({ ...prev, [mode]: data.entries }));
      return data.entries;
    },
    [ensureFreshSession, session],
  );

  const unlockWithSession = useCallback(
    async (s: EnrolledSession) => {
      saveSession(s);
      setSession(s);
      await loadBundle(s);
      setIsUnlocked(true);
      setPhase({ kind: "returningWelcome", displayName: s.display_name });
    },
    [loadBundle],
  );

  const beginEnrollmentProbe = useCallback(async () => {
    setPhase({ kind: "checkingEnrollment" });
    setLastError(null);
    const stored = loadSession();
    if (stored) {
      setSession(stored);
      setPhase({ kind: "returningWelcome", displayName: stored.display_name });
      return;
    }
    try {
      const inspected = await inspectDevice(getDeviceId());
      if (inspected.enrolled) {
        setPhase({ kind: "deviceResume", displayName: inspected.display_name });
        return;
      }
    } catch {
      /* offline — fall through */
    }
    const pending = loadPendingAccessRequest();
    if (pending) {
      setPendingAccessRequestId(pending);
      setPhase({ kind: "accessRequestPending", requestId: pending });
      return;
    }
    setPhase({ kind: "welcome" });
  }, []);

  useEffect(() => {
    void beginEnrollmentProbe();
  }, [beginEnrollmentProbe]);

  const pushPerson = useCallback((id: string) => {
    setPersonStack((prev) => [...prev, id]);
    setSelectedPersonId(id);
  }, []);

  const popPerson = useCallback(() => {
    setPersonStack((prev) => {
      const next = prev.slice(0, -1);
      setSelectedPersonId(next[next.length - 1] ?? null);
      return next;
    });
  }, []);

  useEffect(() => {
    if (selectedPersonId && personStack[personStack.length - 1] !== selectedPersonId) {
      if (personStack.length === 0) {
        setPersonStack([selectedPersonId]);
      }
    }
    if (!selectedPersonId) setPersonStack([]);
  }, [selectedPersonId, personStack]);

  useEffect(() => {
    if (!isUnlocked || !session) return;
    void refreshAll();
  }, [isUnlocked, session, refreshAll]);

  useEffect(() => {
    if (!isUnlocked) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && session) {
        void refreshAll();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isUnlocked, refreshAll, session]);

  const person = useCallback(
    (id: string) => bundle?.people.find((p) => p.person_id === id),
    [bundle],
  );

  const me = useMemo(
    () => (session ? person(session.person_id) : undefined),
    [person, session],
  );

  const value: AppContextValue = {
    phase,
    isUnlocked,
    isWorking,
    lastError,
    session,
    bundle,
    newsFeed,
    upcomingEvents,
    announcements,
    feedbackReplies,
    leaderboardByMode,
    activeTab,
    setActiveTab,
    familyBrowseMode,
    setFamilyBrowseMode,
    selectedPersonId,
    setSelectedPersonId,
    showSettings,
    setShowSettings,
    showInvites,
    setShowInvites,
    showFeedback,
    setShowFeedback,
    showHeritage,
    setShowHeritage,
    bundleLoading,
    bundleLoadError,
    personStack,
    pushPerson,
    popPerson,
    playInGame,
    setPlayInGame,
    showAddEvent,
    setShowAddEvent,
    showAddAnnouncement,
    setShowAddAnnouncement,
    replayOpening: () => {
      resetOpeningSeen();
      setShowHeritage(true);
    },
    replayHeritage: () => {
      resetOpeningSeen();
      setShowHeritage(true);
    },
    resetTabIntrosSeen: () => resetTabIntros(),
    personalAvatarB64,
    personalStepMessage,
    pendingAccessRequestId,
    personalUnlockHelpMatch,
    showPersonalUnlockHelp,
    beginEnrollmentProbe,
    showWelcome: () => {
      setLastError(null);
      resetPersonalUnlockHelp();
      setPhase({ kind: "welcome" });
    },
    showPersonalNameEntry: () => {
      setLastError(null);
      resetPersonalUnlockHelp();
      setPhase({ kind: "personalNameEntry" });
    },
    showInviteEntry: () => {
      setLastError(null);
      setPhase({ kind: "inviteEntry" });
    },
    showAccessHelp: () => {
      setLastError(null);
      setPhase({ kind: "accessHelp" });
    },
    showAccessRequestForm: () => {
      setLastError(null);
      setPhase({ kind: "accessRequestForm" });
    },
    showPendingAccessRequest: () => {
      const id = pendingAccessRequestId ?? loadPendingAccessRequest();
      if (!id) return;
      setPhase({ kind: "accessRequestPending", requestId: id });
    },
    matchPersonalName: async (firstName, lastName) => {
      setIsWorking(true);
      setLastError(null);
      setPersonalStepMessage(null);
      try {
        // Reuse the portable web id — rotating on every name match used to enroll a
        // fresh device and kick the phone when max_devices was 1.
        const deviceId = getDeviceId();
        const match = await personalMatchName(
          firstName.trim(),
          lastName.trim(),
          deviceId,
        );
        setPersonalAvatarB64(match.avatar_b64 ?? null);
        const personalMatch: PersonalMatch = {
          challenge_id: match.challenge_id,
          person_id: match.person_id,
          display_name: match.display_name,
          challenge_path: match.challenge_path,
          partner_prompt_copy: match.partner_prompt_copy,
          already_enrolled: match.already_enrolled,
          avatar_b64: match.avatar_b64,
        };
        setPersonalUnlockHelpMatch(personalMatch);
        setPersonalVerifyFailureCount(0);
        setPhase({
          kind: "personalWelcome",
          match: personalMatch,
        });
      } catch (e) {
        setLastError(e instanceof Error ? e.message : "Could not match name.");
      } finally {
        setIsWorking(false);
      }
    },
    continuePersonalWelcome: (match) => {
      setPhase({ kind: "personalVerifyIntro", match });
    },
    continuePersonalVerifyIntro: (match) => {
      setPhase({ kind: "personalBirthday", match });
    },
    submitPersonalBirthday: async (match, value) => {
      setIsWorking(true);
      setLastError(null);
      setPersonalStepMessage(null);
      try {
        const birthDate = normalizeBirthDateInput(value);
        if (!birthDate) {
          throw new Error("Enter a valid birthday — digits only is fine (MMDDYYYY).");
        }
        const res = await personalVerifyBirthday(getDeviceId(), {
          challenge_id: match.challenge_id,
          birth_date: birthDate,
        });
        if (!res.ok) throw new Error(res.message ?? "Birthday did not match.");
        setPersonalVerifyFailureCount(0);
        const path = match.challenge_path;
        if (path === "partner_state") {
          setPhase({ kind: "personalPartner", match });
        } else if (path === "birthday_phone") {
          setPhase({ kind: "personalPhone", match });
        } else if (path === "birthday_wedding" || path === "partner_wedding") {
          setPhase({ kind: "personalWedding", match });
        } else {
          setPhase({ kind: "personalState", match });
        }
      } catch (e) {
        handlePersonalVerifyError(e, match);
      } finally {
        setIsWorking(false);
      }
    },
    submitPersonalPartner: async (match, value) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const res = await personalVerifyPartner(getDeviceId(), {
          challenge_id: match.challenge_id,
          partner_name: value.trim(),
        });
        if (!res.ok) throw new Error(res.message ?? "Partner did not match.");
        setPersonalVerifyFailureCount(0);
        if (match.challenge_path === "partner_state") {
          setPhase({ kind: "personalState", match });
        } else {
          setPhase({ kind: "personalWedding", match });
        }
      } catch (e) {
        handlePersonalVerifyError(e, match);
      } finally {
        setIsWorking(false);
      }
    },
    submitPersonalPhone: async (match, value) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const res = await personalVerifyPhone(getDeviceId(), {
          challenge_id: match.challenge_id,
          phone: value.trim(),
        });
        if (!res.ok) throw new Error(res.message ?? "Phone did not match.");
        resetPersonalUnlockHelp();
        const s = toSession(
          await personalComplete(match.challenge_id, getDeviceId(), deviceLabel()),
        );
        await unlockWithSession(s);
      } catch (e) {
        if (isAlreadyRegisteredError(e)) {
          setPersonalAvatarB64(null);
          setPersonalStepMessage(null);
          setLastError(null);
          setPhase({ kind: "alreadyRegistered", displayName: match.display_name });
          return;
        }
        handlePersonalVerifyError(e, match);
      } finally {
        setIsWorking(false);
      }
    },
    submitPersonalState: async (match, value) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const res = await personalVerifyState(getDeviceId(), {
          challenge_id: match.challenge_id,
          state: value.trim(),
        });
        if (!res.ok) throw new Error(res.message ?? "State did not match.");
        resetPersonalUnlockHelp();
        const s = toSession(
          await personalComplete(match.challenge_id, getDeviceId(), deviceLabel()),
        );
        await unlockWithSession(s);
      } catch (e) {
        if (isAlreadyRegisteredError(e)) {
          setPersonalAvatarB64(null);
          setPersonalStepMessage(null);
          setLastError(null);
          setPhase({ kind: "alreadyRegistered", displayName: match.display_name });
          return;
        }
        handlePersonalVerifyError(e, match);
      } finally {
        setIsWorking(false);
      }
    },
    submitPersonalWedding: async (match, value) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const weddingDate = normalizeBirthDateInput(value);
        if (!weddingDate) {
          throw new Error("Enter a valid wedding date — digits only is fine (MMDDYYYY).");
        }
        const res = await personalVerifyWedding(getDeviceId(), {
          challenge_id: match.challenge_id,
          wedding_date: weddingDate,
        });
        if (!res.ok) throw new Error(res.message ?? "Wedding date did not match.");
        resetPersonalUnlockHelp();
        const s = toSession(
          await personalComplete(match.challenge_id, getDeviceId(), deviceLabel()),
        );
        await unlockWithSession(s);
      } catch (e) {
        if (isAlreadyRegisteredError(e)) {
          setPersonalAvatarB64(null);
          setPersonalStepMessage(null);
          setLastError(null);
          setPhase({ kind: "alreadyRegistered", displayName: match.display_name });
          return;
        }
        handlePersonalVerifyError(e, match);
      } finally {
        setIsWorking(false);
      }
    },
    submitPersonalUnlockHelp: async (match, reason) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!("geolocation" in navigator)) {
            reject(new Error("Location access is required to send an access request."));
            return;
          }
          navigator.geolocation.getCurrentPosition(resolve, () => {
            reject(new Error("Location access is required to send an access request."));
          }, { enableHighAccuracy: true, timeout: 20000 });
        });
        const res = await requestAccess({
          device_id: getDeviceId(),
          requester_name: match.display_name,
          sharer_name: "personal unlock",
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_m: position.coords.accuracy,
          suggested_person_id: match.person_id,
          reason,
          device_label: deviceLabel(),
        });
        savePendingAccessRequest(res.request_id);
        setPendingAccessRequestId(res.request_id);
        setPhase({ kind: "accessRequestPending", requestId: res.request_id });
      } catch (e) {
        setLastError(e instanceof Error ? e.message : "Request failed.");
      } finally {
        setIsWorking(false);
      }
    },
    inspectInviteCode: async (code) => {
      const normalized = normalizeInviteCode(code);
      if (!normalized) throw new Error("Enter a valid invite code.");
      return inspectInvite(normalized);
    },
    redeemInviteCode: async (code) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const normalized = normalizeInviteCode(code);
        if (!normalized) throw new Error("Enter a valid invite code.");
        const deviceId = rotateDeviceId();
        const s = toSession(
          await redeemInvite(normalized, deviceId, deviceLabel()),
        );
        clearPendingAccessRequest();
        setPendingAccessRequestId(null);
        await unlockWithSession(s);
      } catch (e) {
        setLastError(e instanceof Error ? e.message : "Redeem failed.");
      } finally {
        setIsWorking(false);
      }
    },
    submitAccessRequest: async (payload) => {
      setIsWorking(true);
      setLastError(null);
      try {
        const res = await requestAccess({
          device_id: rotateDeviceId(),
          requester_name: payload.requester_name,
          sharer_name: payload.sharer_name,
          latitude: payload.latitude,
          longitude: payload.longitude,
          accuracy_m: payload.accuracy_m ?? null,
          suggested_person_id: payload.suggested_person_id ?? null,
          reason: payload.reason ?? null,
          device_label: deviceLabel(),
        });
        savePendingAccessRequest(res.request_id);
        setPendingAccessRequestId(res.request_id);
        setPhase({ kind: "accessRequestPending", requestId: res.request_id });
      } catch (e) {
        setLastError(e instanceof Error ? e.message : "Request failed.");
      } finally {
        setIsWorking(false);
      }
    },
    pollAccessRequest: async () => {
      const id = pendingAccessRequestId ?? loadPendingAccessRequest();
      if (!id) return;
      setIsWorking(true);
      setLastError(null);
      try {
        const res = await requestAccessStatus(getDeviceId(), id);
        if (res.status === "approved") {
          clearPendingAccessRequest();
          setPendingAccessRequestId(null);
          setPhase({ kind: "inviteEntry" });
        } else if (res.status === "denied") {
          clearPendingAccessRequest();
          setPendingAccessRequestId(null);
          setLastError("Your access request was not approved.");
          setPhase({ kind: "welcome" });
        }
      } catch (e) {
        setLastError(e instanceof Error ? e.message : "Could not check status.");
      } finally {
        setIsWorking(false);
      }
    },
    resumeEnrolledDevice: async () => {
      setIsWorking(true);
      setLastError(null);
      try {
        const s = toSession(await resumeDevice(getDeviceId()));
        await unlockWithSession(s);
      } catch (e) {
        setLastError(e instanceof Error ? e.message : "Could not resume device.");
      } finally {
        setIsWorking(false);
      }
    },
    abandonEnrolledDevice: async () => {
      setIsWorking(true);
      setLastError(null);
      try {
        try {
          const s = toSession(await resumeDevice(getDeviceId()));
          await leaveDevice(s);
        } catch {
          /* already unbound or offline */
        }
        clearSession();
        clearDeviceId();
        revokePhotoUrls();
        setSession(null);
        setBundle(null);
        setIsUnlocked(false);
        setPhase({ kind: "inviteEntry" });
      } finally {
        setIsWorking(false);
      }
    },
    continueIntoApp: async () => {
      if (!session) return;
      setIsWorking(true);
      setLastError(null);
      try {
        let s = session;
        if (isSessionExpired(session)) {
          try {
            s = toSession(await refreshSession(session));
          } catch (error) {
            if (isDefinitiveEnrollmentRevoke(error)) throw error;
            s = toSession(await resumeDevice(getDeviceId()));
          }
        }
        saveSession(s);
        setSession(s);
        await loadBundle(s);
        setIsUnlocked(true);
        setShowHeritage(!hasSeenOpening() && !shouldSkipOpening());
      } catch (e) {
        if (isDefinitiveEnrollmentRevoke(e)) {
          clearSession();
          setSession(null);
          setBundle(null);
          setIsUnlocked(false);
          setLastError(
            "Access on this device was revoked. Sign in again, or ask for a personal invitation code.",
          );
          setPhase({ kind: "welcome" });
        } else {
          setLastError(e instanceof Error ? e.message : "Could not open app.");
        }
      } finally {
        setIsWorking(false);
      }
    },
    signOut: async () => {
      setIsWorking(true);
      try {
        if (session) await leaveDevice(session);
      } catch {
        /* best effort */
      }
      clearSession();
      clearDeviceId();
      revokePhotoUrls();
      setSession(null);
      setBundle(null);
      setIsUnlocked(false);
      setIsWorking(false);
      setPhase({ kind: "welcome" });
    },
    refreshAll,
    refreshLeaderboard,
    person,
    me,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
